import React, { useMemo } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

interface DecayRateData {
  channel: string;
  decay_rate: number;
}

interface DecayCurvesProps {
  data?: DecayRateData[];
  title?: string;
}

const colors = [
  '#8884d8', '#82ca9d', '#ffc658', '#ff7c7c', '#8dd1e1',
  '#a4de6c', '#d0ed57', '#fa8c16', '#eb2f96'
];

export const DecayCurves: React.FC<DecayCurvesProps> = ({
  data,
  title = 'True Adstock Decay (Decay over Time)',
}) => {
  // Generate curve data (0 to 12 weeks)
  const weeks = useMemo(() => Array.from({ length: 13 }, (_, i) => i), []); // 0 to 12

  const chartData = useMemo(() => {
    if (!Array.isArray(data) || data.length === 0) return [];

    return weeks.map(week => {
      const point: any = { week };
      data.forEach(channelData => {
        // Geometric decay: value = rate ^ week
        point[channelData.channel] = Math.pow(channelData.decay_rate, week);
      });
      return point;
    });
  }, [data, weeks]);

  if (!Array.isArray(data) || data.length === 0) {
    return (
      <div className="w-full h-full bg-white p-6 rounded-lg border border-gray-200">
        <h3 className="text-lg font-bold text-gray-900 mb-4">{title}</h3>
        <div className="flex items-center justify-center h-96 bg-gray-50 rounded">
          <p className="text-gray-500">No decay rate data available from model results</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full bg-white p-6 rounded-lg border border-gray-200">
      <h3 className="text-lg font-bold text-gray-900 mb-4">{title}</h3>

      <ResponsiveContainer width="100%" height={400}>
        <LineChart data={chartData} margin={{ top: 5, right: 30, left: 10, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="week"
            label={{ value: 'Weeks Since Exposure', position: 'insideBottom', offset: -5 }}
          />
          <YAxis
            label={{ value: 'Effect Remaining (%)', angle: -90, position: 'insideLeft' }}
            tickFormatter={(val) => `${(val * 100).toFixed(0)}%`}
          />
          <Tooltip
            formatter={(value: number, name: string) => [`${(value * 100).toFixed(1)}%`, name]}
            labelFormatter={(label) => `Week ${label}`}
          />
          <Legend />
          {data.map((channelData, index) => (
            <Line
              key={channelData.channel}
              type="monotone"
              dataKey={channelData.channel}
              stroke={colors[index % colors.length]}
              strokeWidth={2}
              dot={{ r: 3 }}
              activeDot={{ r: 6 }}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>

      <div className="mt-4 text-sm text-gray-600">
        <p>
          <strong>Interpretation:</strong> This chart shows how the media effect decays over time after initial exposure.
          A flatter curve means the ad effect lasts longer (higher carryover), while a steep drop indicates the effect is short-lived.
          This helps in planning flighting vs. always-on strategies.
        </p>
      </div>
    </div>
  );
};
